import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthCard } from '../components/AuthCard';
import { AuthLayout } from '../components/AuthLayout';
import { useAuth } from '../hooks/useAuth';
import { POST_LOGIN_ROUTE } from '../routes';
import { authService } from '../services/authService';
import type { SignInFlowState } from '../types';
import type { SignInFormValues } from '../schemas';
import { NewPasswordRequiredStep } from '../steps/NewPasswordRequiredStep';
import { SignInStep } from '../steps/SignInStep';
import { SmsConfirmationStep } from '../steps/SmsConfirmationStep';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [flow, setFlow] = useState<SignInFlowState>({
    step: 'username_password',
    username: '',
    rememberMe: true,
    error: null,
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate(POST_LOGIN_ROUTE, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  async function handleSignIn(values: SignInFormValues): Promise<void> {
    const result = await authService.signIn(values.username, values.password, values.rememberMe);
    handleNextStep(result, values.username, values.rememberMe);
  }

  async function handleNewPasswordRequired(newPassword: string): Promise<void> {
    const result = await authService.confirmSignIn(newPassword);
    handleNextStep(result, flow.username, flow.rememberMe);
  }

  async function handleSmsConfirmation(code: string): Promise<void> {
    const result = await authService.confirmSignIn(code);
    handleNextStep(result, flow.username, flow.rememberMe);
  }

  function handleNextStep(
    result: { isSignedIn: boolean; nextStep: { signInStep: string } },
    username: string,
    rememberMe: boolean
  ): void {
    if (result.isSignedIn) {
      navigate(POST_LOGIN_ROUTE, { replace: true });
      return;
    }

    const { signInStep } = result.nextStep;

    if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      setFlow({ step: 'new_password_required', username, rememberMe, error: null });
    } else if (
      signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
      signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
    ) {
      setFlow({ step: 'sms_confirmation', username, rememberMe, error: null });
    } else {
      setFlow((prev) => ({
        ...prev,
        error: `Paso de autenticación no soportado: ${signInStep}`,
      }));
    }
  }

  return (
    <AuthLayout>
      {flow.step === 'username_password' && (
        <AuthCard title="Iniciar sesión" subtitle="Sistema DPV">
          <SignInStep onSubmit={handleSignIn} />
        </AuthCard>
      )}

      {flow.step === 'new_password_required' && (
        <AuthCard title="Nueva contraseña requerida">
          <NewPasswordRequiredStep onSubmit={handleNewPasswordRequired} />
        </AuthCard>
      )}

      {flow.step === 'sms_confirmation' && (
        <AuthCard title="Verificación en dos pasos">
          <SmsConfirmationStep onSubmit={handleSmsConfirmation} />
        </AuthCard>
      )}
    </AuthLayout>
  );
}
